import React, { useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { LinkedBanners } from '../../../redux/seo/types';
import useUserTracking from '../../../hooks/flags/useUserTracking';
import Banner from '../../Generic/Banner';
import EventsListener, { createShopToClubBannerEvents, EventDispatcher } from '../../../../common/events';
import { initShopToClubBannerABTest } from '../../../redux/shopToClubBanner/shopToClubBannerABTest';
import { ShopToClubBannerABTestInitialState, ShopToClubBannerABTestState } from '../../../redux/shopToClubBanner/types';
import useGa from '../../../hooks/useGa';

const StyledContainer = styled.div`
    padding: 20px 10px;
`;

interface Props {
    shopToClubFooterBanners: LinkedBanners[];
    isFullWidthBanner?: boolean;
    categoryId?: string;
    shopToClubABTest?: ShopToClubBannerABTestState;
}

const ShopToClubBanner: React.SFC<Props> = ({
    shopToClubFooterBanners = [],
    categoryId = '',
    shopToClubABTest = ShopToClubBannerABTestInitialState,
}) => {
    const { isRegistered } = useUserTracking();
    const bannerRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    const { linkUrl, imgSrc, desktopImg, categories } = shopToClubABTest;

    // allow banner attributes be customized from 3rd party platform (eg: google optimize)
    useEffect(() => {
        dispatch(initShopToClubBannerABTest(categoryId));
    }, [categoryId, dispatch]);

    useEffect(() => {
        const eventDispatcher = EventDispatcher();
        const events = createShopToClubBannerEvents(dispatch);
        const eventsHandler = EventsListener(events);
        // trigger event to notify 3rd party platform the event listener is ready.
        eventDispatcher.triggerShop2ClubBannerIsReady();
        return () => eventsHandler.dispose();
        // Setup events only one time on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const gaTracker = useGa();

    const isABTestTarget = !!(
        !isRegistered &&
        categories &&
        categoryId &&
        categories.split(',').indexOf(categoryId) > -1
    );

    return !isRegistered ? (
        <StyledContainer ref={bannerRef}>
            {shopToClubFooterBanners.map(banner => {
                return (
                    <Banner
                        key={isABTestTarget && linkUrl ? linkUrl : banner.bannerLink}
                        shouldLazyLoadBanner
                        targetUrl={isABTestTarget && linkUrl ? linkUrl : banner.bannerLink}
                        mobileImage={isABTestTarget && imgSrc ? imgSrc : banner.bannerImage}
                        desktopImage={isABTestTarget && desktopImg ? desktopImg : banner.desktopBannerImage}
                        trackingFn={() => {
                            gaTracker.shopToClub.trackProductListingBannerClick(
                                isABTestTarget && linkUrl ? linkUrl : banner.bannerLink
                            );
                        }}
                        trackImpressionFunction={() => {
                            gaTracker.shopToClub.trackProductListingBannerImpression(
                                isABTestTarget && linkUrl ? linkUrl : banner.bannerLink
                            );
                        }}
                    />
                );
            })}
        </StyledContainer>
    ) : (
        <></>
    );
};

export default ShopToClubBanner;
